import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { ChatCircleDots, BellRinging, Lock, Lightning } from "@phosphor-icons/react";

// Room Chat entry point. Replaces the client WhatsApp icon for petugas and is an
// ADDITIONAL button for admin. Shows a red bell + unread count when there are unread
// messages for THIS actor; a plain chat icon otherwise. Only renders for Homecare
// reservations. If the room is not yet active it shows a clear disabled status.
//
// Props:
//   reservation: serialized reservation row (needs id, lokasi, homecare_assignment_status)
//   roomInfo: { room_id, unread } from useHomecareRooms().byReservation[reservation.id] (or undefined)
//   size: "sm" | "md"
export default function RoomChatButton({ reservation: r, roomInfo, size = "sm" }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activating, setActivating] = useState(false);
  const role = user?.role;
  const isStaff = role === "petugas";
  const isAdmin = role === "admin" || role === "super_admin";
  if (!isStaff && !isAdmin) return null;

  const homecare = (r?.lokasi || "") === "Homecare" || Number(r?.biaya_homecare || 0) > 0;
  if (!homecare) return null;

  const pad = size === "md" ? "px-4 py-2 text-sm" : "px-3 py-1.5 text-xs";

  // Targeted sync SATU reservasi (admin): buat room + 1 WA aktivasi bila syarat terpenuhi.
  // Idempoten di backend — tidak menggandakan room/token/WA.
  const activate = async () => {
    if (activating) return;
    setActivating(true);
    try {
      const res = await api.post("/homecare/sync-reservation", { reservation_id: r.id });
      if (res.data?.room_id) {
        toast.success("Room Chat aktif.");
        setTimeout(() => window.location.reload(), 700);
      } else {
        toast.info("Room belum dapat diaktifkan — periksa status reservasi & pembayaran.");
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setActivating(false);
    }
  };

  // Room not active yet -> clear status, no navigation. Admin gets a targeted-sync action.
  if (!roomInfo || !roomInfo.room_id) {
    const needsAdmin = r?.homecare_assignment_status === "needs_admin";
    return (
      <span className="inline-flex items-center gap-1.5">
        <span
          data-testid={`room-chat-inactive-${r.id}`}
          title={needsAdmin ? "Perlu penugasan admin" : "Chat belum aktif"}
          className={`inline-flex items-center gap-1.5 rounded-lg bg-slate-100 font-semibold text-slate-400 ${pad}`}
        >
          <Lock size={size === "md" ? 16 : 14} weight="bold" />
          {needsAdmin ? "Perlu penugasan admin" : "Chat belum aktif"}
        </span>
        {isAdmin && !needsAdmin && (
          <button
            data-testid={`room-chat-activate-${r.id}`}
            onClick={activate}
            disabled={activating}
            title="Aktifkan Room Chat untuk reservasi ini (bila pembayaran/konfirmasi sudah memenuhi syarat)"
            className={`inline-flex items-center gap-1 rounded-lg bg-[#EEE6FF] font-bold text-[#6A38C7] transition-transform active:scale-95 disabled:opacity-50 ${pad}`}
          >
            <Lightning size={size === "md" ? 16 : 14} weight="fill" />
            {activating ? "Memproses…" : "Aktifkan"}
          </button>
        )}
      </span>
    );
  }

  const unread = roomInfo.unread || 0;
  const open = () => {
    if (isStaff) navigate(`/homecare-chat/${roomInfo.room_id}`);
    else navigate(`/homecare-chat-admin?room=${roomInfo.room_id}`);
  };

  return (
    <button
      data-testid={`room-chat-btn-${r.id}`}
      onClick={open}
      className={`relative inline-flex items-center gap-1.5 rounded-lg font-bold transition-transform active:scale-95 ${pad} ${
        unread > 0 ? "bg-rose-50 text-rose-600 hover:bg-rose-100" : "bg-[#EEE6FF] text-[#6A38C7] hover:bg-[#e0d2ff]"
      }`}
    >
      {unread > 0 ? <BellRinging size={size === "md" ? 18 : 15} weight="fill" /> : <ChatCircleDots size={size === "md" ? 18 : 15} weight="fill" />}
      Room Chat
      {unread > 0 && (
        <span data-testid={`room-chat-unread-${r.id}`} className="ml-0.5 inline-flex min-w-[18px] items-center justify-center rounded-full bg-rose-500 px-1 py-0.5 text-[10px] font-bold leading-none text-white">
          {unread}
        </span>
      )}
    </button>
  );
}
